import type { VoucherApplicationState } from './voucher.types';

/**
 * Credit System Types
 *
 * These types define the structure for the credit-based payment system
 * where users purchase credit packages to use AI features.
 */

/**
 * Available credit packages with their prices in CZK
 */
export enum CreditPackagePrice {
    PACKAGE_1 = 1,
    PACKAGE_500 = 500,
    PACKAGE_1000 = 1000,
    PACKAGE_2000 = 2000,
    PACKAGE_5000 = 5000,
}

/**
 * Credit package definition
 */
export interface CreditPackage {
    id: string;
    price: number;
    credits: number;
    currency: 'CZK' | 'EUR';
    label: string;
    description?: string;
    descriptionSk?: string | null;
    isPopular?: boolean;
    /** Admin-defined sale badge text. Empty/undefined = no badge. */
    saleBadge?: string;
}

/**
 * AI feature types that consume credits
 */
export enum AiFeatureType {
    LANGUAGE_CORRECTION = 'language_correction',
    PAGE_CORRECTION = 'page_correction',
    PAGE_CORRECTION_BASIC = 'page_correction_basic',
    PAGE_CORRECTION_ADVANCED = 'page_correction_advanced',
    CITATION_GENERATION = 'citation_generation',
    DOCUMENT_UPLOAD = 'document_upload',
}

/**
 * AI feature cost configuration
 */
export interface AiFeatureCost {
    featureType: AiFeatureType;
    label: string;
    costPerUnit: number;
    unit: string; // e.g., "80 words", "1 page", "1 citation", "1 document"
}

/**
 * Transaction type
 */
export enum TransactionType {
    CREDIT_PURCHASE = 'creditPurchase',
    CREDIT_USAGE = 'creditUsage',
    CREDIT_REFUND = 'creditRefund',
}

/**
 * Transaction record
 */
export interface CreditTransaction {
    id: string;
    type: TransactionType;
    amount: number; // positive for purchases/refunds, negative for usage
    credits: number; // credits added or deducted
    description: string;
    featureType?: AiFeatureType;
    packageId?: string;
    createdAt: Date;
    metadata?: {
        documentId?: string;
        wordCount?: number;
        pageCount?: number;
        [key: string]: any;
    };
}

/**
 * Wallet balance information
 */
export interface WalletBalance {
    totalCredits: number;
    usedCredits: number;
    availableCredits: number;
    totalSpent: number; // in currency
    currency: 'CZK' | 'EUR';
}

/**
 * Expense breakdown by feature type
 */
export interface ExpenseBreakdown {
    featureType: AiFeatureType;
    label: string;
    creditsUsed: number;
    usageCount: number;
    percentage: number;
}

/**
 * Package selection for checkout
 */
export interface PackageSelection {
    package: CreditPackage;
    quantity: number;
}

/**
 * Checkout summary
 */
export interface CheckoutSummary {
    selections: PackageSelection[];
    totalPrice: number;
    totalCredits: number;
    currency: 'CZK' | 'EUR';
}

/**
 * Billing information for checkout
 */
export interface BillingInformation {
    fullName: string;
    email: string;
    companyName?: string;
    street: string;
    city: string;
    postalCode: string;
    country: string;
    vatId?: string;
}

/**
 * Payment confirmation details
 */
export interface PaymentConfirmation {
    transactionId: string;
    creditsPurchased: number;
    amountPaid: number;
    currency: 'CZK' | 'EUR';
    purchasedAt: Date;
    billingInfo: BillingInformation;
    packages: PackageSelection[];
    appliedVoucher?: VoucherApplicationState;
}

